import { cast, detach, flow, Instance, onSnapshot, types } from 'mobx-state-tree';
import { Moment } from 'moment';
import { AddToast } from 'react-toast-notifications';
import { NullableMomentDate } from '@models/shared/NullableMomentDate';
import {
  TimelineGroupType,
  TimelineItemType,
  TimetableSlotCountType,
  checkForLecturerConflict,
  checkForLocationConflict,
} from '@modules/timetable';
import { ApolloQueryResult } from '@apollo/client';
import { TimelineItemTypeType } from '@app-types/timetable';
import {
  BATCH_CLASS_BATCH_SUBJECT_CONFIGURATIONS,
  batch_class_batch_subject_configurationsQuery,
  batch_class_batch_subject_configurationsQueryVariables,
  BATCH_CLASS_BATCH_SUBJECT_EXTRA_SLOT,
  batch_class_batch_subject_extra_slotQuery,
  batch_class_batch_subject_extra_slotQueryVariables,
  batches_for_timetable_selectQuery,
} from '@app-types/graphql-types';
import { apolloClient } from '@lib/apollo';
import { Batch } from './Batch';
import { TimeSlotConfiguration } from './TimeSlotConfiguration';
import {
  SetInitialTimetableSlotCountDataArgs,
  SetTimeSlotConfigurationArgs,
  UpdateSelectedTimelineItemTimeArgs,
} from './Timetable/types';
import { TimelineItem } from './TimelineItem';
import { TimelineGroup } from './TimelineGroup';
import { TimetableSlotCount } from './TimetableSlotCount';
import { setBatch } from './Timetable/functions/setBatch';
import { setTimeSlotConfiguration } from './Timetable/functions/setTimeSlotConfiguration';
import { loadTimeSlotConfigurationFromDB } from './Timetable/functions/loadTimeSlotConfigurationFromDB';
import { setTimelineGroupsAccordingToBatch } from './Timetable/functions/setTimelineGroupsAccordingToBatch';
import { refreshTimetable } from './Timetable/functions/refreshTimetable';
import { loadTimelineItemsFromDB } from './Timetable/functions/loadTimelineItemsFromDB';

export const Timetable = types
  .model('InquiryModel', {
    dragHoverTime: types.maybeNull(types.string),
    classId: '',
    batch: types.maybeNull(Batch),
    week: types.maybeNull(NullableMomentDate),
    timeSlotConfigurations: types.maybeNull(types.array(TimeSlotConfiguration)),
    timelineItems: types.optional(types.array(TimelineItem), []),
    timelineGroups: types.maybeNull(types.array(TimelineGroup)),
    timetableSlotCounts: types.optional(types.array(TimetableSlotCount), []),
    loadingTimeSlotConfigurations: false,
    timeSlotConfigurationsLoaded: false,
    selectedTimelineItem: types.maybe(types.safeReference(TimelineItem)),
    selectedDraggableItem: types.maybe(types.string),
    selectedLecturer: types.maybe(types.string),
    selectedLocation: types.maybe(types.string),
    timelineItemConfigVisible: false,
    timelineLoading: types.maybe(types.boolean),
    loadingTimelineItems: false,
    removedTimelineItems: types.optional(types.array(types.string), []),
  })
  .actions((self) => ({
    setDragHoverTime(dragHoverTime: string | null) {
      self.dragHoverTime = dragHoverTime;
    },
    setClassId(classId: string) {
      self.classId = classId;
    },
    clearState() {
      self.dragHoverTime = null;
      self.classId = '';
      self.batch = null;
      self.week = null;
      self.timeSlotConfigurations = null;
      self.timelineItems = cast([]);
      self.timelineGroups = cast([]);
      self.timetableSlotCounts = cast([]);
      self.loadingTimeSlotConfigurations = false;
      self.timeSlotConfigurationsLoaded = false;
      self.selectedTimelineItem = undefined;
      self.selectedDraggableItem = undefined;
      self.selectedLecturer = undefined;
      self.selectedLocation = undefined;
      self.timelineItemConfigVisible = false;
      self.timelineLoading = false;
      self.loadingTimelineItems = false;
      self.removedTimelineItems = cast([]);
    },
    setBatch(batch: batches_for_timetable_selectQuery['active_batch'][0] | null) {
      setBatch({ self, batch });
      setTimelineGroupsAccordingToBatch({ self, batch });
    },
    updateBatchClassBatchSubjectConfigurations: flow(function* updateBatchClassBatchSubjectConfigurationsAsync() {
      if (self.batch) {
        const batchClassBatchSubjectConfigurationData: ApolloQueryResult<batch_class_batch_subject_configurationsQuery> =
          yield apolloClient.query<
            batch_class_batch_subject_configurationsQuery,
            batch_class_batch_subject_configurationsQueryVariables
          >({
            query: BATCH_CLASS_BATCH_SUBJECT_CONFIGURATIONS,
            variables: {
              where: {
                batch_class: {
                  batchId: {
                    _eq: self.batch.id,
                  },
                },
              },
            },
            fetchPolicy: 'no-cache',
          });

        if (batchClassBatchSubjectConfigurationData?.data?.batch_class_batch_subject_configurations) {
          self.batch.batch_classes.forEach((batchClass) => {
            const batchSubjectCountsForClass =
              batchClassBatchSubjectConfigurationData.data.batch_class_batch_subject_configurations.filter(
                (batchClassBatchSubjectConfiguration) =>
                  batchClassBatchSubjectConfiguration.batchClassId === batchClass.id,
              );

            batchClass.batch_class_batch_subject_configurations = cast(
              batchSubjectCountsForClass.map((batchSubjectCountForClass) => ({
                batchSubjectConfigurationId: batchSubjectCountForClass.batchSubjectConfigurationId,
                count: batchSubjectCountForClass.count,
              })),
            );
          });
        }
      }
    }),
    updateBatchClassBatchSubjectExtraSlots: flow(function* updateBatchClassBatchSubjectExtraSlotsAsync() {
      if (self.batch) {
        const batchClassBatchSubjectExtraSlotData: ApolloQueryResult<batch_class_batch_subject_extra_slotQuery> =
          yield apolloClient.query<
            batch_class_batch_subject_extra_slotQuery,
            batch_class_batch_subject_extra_slotQueryVariables
          >({
            query: BATCH_CLASS_BATCH_SUBJECT_EXTRA_SLOT,
            variables: {
              where: {
                batch_class: {
                  batchId: {
                    _eq: self.batch.id,
                  },
                },
              },
            },
            fetchPolicy: 'no-cache',
          });

        if (batchClassBatchSubjectExtraSlotData?.data?.batch_class_batch_subject_extra_slot) {
          self.batch.batch_classes.forEach((batchClass) => {
            const batchSubjectExtraSlotCountsForClass =
              batchClassBatchSubjectExtraSlotData.data.batch_class_batch_subject_extra_slot.filter(
                (batchClassBatchSubjectExtraSlot) => batchClassBatchSubjectExtraSlot.batchClassId === batchClass.id,
              );

            batchClass.batch_class_batch_subject_extra_slot = cast(
              batchSubjectExtraSlotCountsForClass.map((batchSubjectCountForClass) => ({
                batchSubjectExtraSlotId: batchSubjectCountForClass.batchSubjectExtraSlotId,
                count: batchSubjectCountForClass.count,
              })),
            );
          });
        }
      }
    }),
    setWeek(week: Moment | null) {
      self.selectedTimelineItem = undefined;
      self.timeSlotConfigurationsLoaded = false;
      self.timeSlotConfigurations = null;
      self.week = week;

      flow(loadTimelineItemsFromDB(self))();
    },
    setTimeSlotConfiguration({ timeSlotConfigurationsData, applyToAll }: SetTimeSlotConfigurationArgs) {
      setTimeSlotConfiguration({ self, timeSlotConfigurationsData, applyToAll });
    },
    loadTimeSlotConfigurationFromDB() {
      flow(loadTimeSlotConfigurationFromDB(self))();
    },
    loadTimelineItemsFromDB() {
      self.timelineItems = cast(self.timelineItems.filter((item) => item.itemType === 'interval'));

      flow(loadTimelineItemsFromDB(self))();
    },
    setTimelineItems(timelineItems: TimelineItemType[]) {
      self.selectedTimelineItem = undefined;
      self.timelineItems = cast(timelineItems);

      refreshTimetable();
    },
    setTimelineGroups(timelineGroups: TimelineGroupType[]) {
      self.timelineGroups = cast(timelineGroups);
    },
    setInitialTimetableSlotCounts({ subjectData }: SetInitialTimetableSlotCountDataArgs) {
      const timetableSlotsTotalSlotCount: TimetableSlotCountType[] = [];

      if (self.batch?.batch_classes && self.week) {
        // eslint-disable-next-line no-unused-expressions
        self.batch?.batch_classes.forEach((batchClass) => {
          if (subjectData) {
            subjectData.batch_subject_configuration.forEach((batchSubject) => {
              const existingItemCount =
                batchClass.batch_class_batch_subject_configurations.find(
                  (batchSubjectConfiguration) =>
                    batchSubjectConfiguration.batchSubjectConfigurationId === batchSubject.id,
                )?.count || 0;

              timetableSlotsTotalSlotCount.push({
                id: batchSubject.id,
                classId: batchClass.id,
                availableSlotCount: batchSubject.totalSlots - existingItemCount,
              });

              batchSubject.batch_subject_extra_slots.forEach((subjectExtra) => {
                const existingExtraItemCount =
                  batchClass.batch_class_batch_subject_extra_slot.find(
                    (batchSubjectExtraSlot) => batchSubjectExtraSlot.batchSubjectExtraSlotId === subjectExtra.id,
                  )?.count || 0;

                // eslint-disable-next-line no-unused-expressions,@typescript-eslint/no-unused-expressions
                self.timelineItems.filter(
                  (timelineItem) =>
                    timelineItem.batchClassID.split('/')[1] === batchClass.id &&
                    timelineItem.elementID === subjectExtra.id &&
                    timelineItem.startTime.week() === self.week?.week(),
                ).length;

                timetableSlotsTotalSlotCount.push({
                  id: subjectExtra.id,
                  classId: batchClass.id,
                  availableSlotCount: (subjectExtra.totalSlots || 0) - existingExtraItemCount,
                });
              });
            });
          }
        });
      }

      self.timetableSlotCounts = cast(timetableSlotsTotalSlotCount);
    },
    resetTimelineItems() {
      if (self.timelineItems) {
        // Reset everything except for intervals
        self.timelineItems = cast(
          self.timelineItems.filter((timelineItem) => timelineItem.itemType === TimelineItemTypeType.interval),
        );

        refreshTimetable();
      }
    },
    setSelectedTimelineItem(itemId?: string) {
      self.selectedLocation = undefined;
      self.selectedDraggableItem = undefined;
      self.selectedLecturer = undefined;

      if (itemId) {
        self.selectedTimelineItem = self.timelineItems.find((timelineItem) => timelineItem.id === itemId);

        if (self.selectedTimelineItem) {
          self.selectedLecturer = self.selectedTimelineItem.lecturerID || undefined;
          self.selectedLocation = self.selectedTimelineItem.locationID;
        }
      } else {
        self.selectedTimelineItem = undefined;
      }
    },
    removeSelectedTimelineItem(addToast: AddToast) {
      if (self.selectedTimelineItem) {
        if (self.selectedTimelineItem.itemType === TimelineItemTypeType.interval) {
          addToast('Error. Please use the timetable configuration panel to modify intervals.', {
            appearance: 'error',
            autoDismiss: true,
          });
        } else {
          const itemToBeDeleted = self.selectedTimelineItem;
          self.selectedTimelineItem = undefined;
          detach(itemToBeDeleted);
          const itemToBeDeletedIndex = self.timetableSlotCounts.findIndex(
            (timetableSlot) => timetableSlot.id === itemToBeDeleted.elementID,
          );

          if (itemToBeDeletedIndex > -1) {
            self.timetableSlotCounts[itemToBeDeletedIndex].setTimetableSlotCount(
              self.timetableSlotCounts[itemToBeDeletedIndex].availableSlotCount + 1,
            );
          }

          if (itemToBeDeleted.dbId) {
            self.removedTimelineItems.push(itemToBeDeleted.dbId);
          }

          refreshTimetable();
        }
      }
    },
    setSelectedDraggableItem(selectedDraggableItem?: string) {
      self.selectedLocation = undefined;
      self.selectedTimelineItem = undefined;

      if (self.selectedDraggableItem === selectedDraggableItem) {
        self.selectedDraggableItem = undefined;
      } else {
        self.selectedDraggableItem = selectedDraggableItem;
      }
    },
    setSelectedLecturer: flow(function* setSelectedLecturerAsync(selectedLecturer?: string) {
      if (self.selectedTimelineItem) {
        const timelineItemPreviousLecturer = self.selectedTimelineItem.lecturerID;

        self.selectedTimelineItem.lecturerID = selectedLecturer || null;
        const isTargetValid = yield checkForLecturerConflict({
          timelineItems: self.timelineItems,
          item: self.selectedTimelineItem,
          endTime: self.selectedTimelineItem.endTime,
          startTime: self.selectedTimelineItem.startTime,
        });

        if (isTargetValid) {
          self.selectedLecturer = selectedLecturer;
        } else {
          self.selectedTimelineItem.lecturerID = timelineItemPreviousLecturer;
        }
      } else {
        self.selectedLecturer = selectedLecturer;
      }
    }),
    setSelectedLocation: flow(function* setSelectedLocationAsync(selectedLocation?: string) {
      if (self.selectedTimelineItem) {
        const timelineItemPreviousLocation = self.selectedTimelineItem.locationID;
        self.selectedTimelineItem.locationID = selectedLocation;

        const isTargetValid = yield checkForLocationConflict({
          timelineItems: self.timelineItems,
          item: self.selectedTimelineItem,
          endTime: self.selectedTimelineItem.endTime,
          startTime: self.selectedTimelineItem.startTime,
        });

        if (isTargetValid) {
          self.selectedLocation = selectedLocation;
        } else {
          self.selectedTimelineItem.locationID = timelineItemPreviousLocation;
        }
      } else {
        self.selectedLocation = selectedLocation;
      }
    }),
    setTimelineItemConfigVisible(modalVisible: boolean) {
      self.timelineItemConfigVisible = modalVisible;
    },
    setTimelineLoading(timelineItemLoading: boolean) {
      self.timelineLoading = timelineItemLoading;
    },
    updateSelectedTimelineItemTime({ startTime, endTime, groupID }: UpdateSelectedTimelineItemTimeArgs) {
      if (self.selectedTimelineItem) {
        self.timelineLoading = true;

        self.selectedTimelineItem.moveItem({ startTime, endTime, groupID });
      }
    },
    // Lifecycle hooks
    afterCreate() {
      onSnapshot(self, flow(loadTimeSlotConfigurationFromDB(self)));
    },
  }));

export type TimetableModelType = Instance<typeof Timetable>;
